import { createContext, useContext } from 'react';

import { RESTAURANTE_CON_TODO, type Negocio, type Rubro } from './negocio';

/**
 * Cómo se nombran las cosas del almacén según el rubro.
 *
 * Es el mismo criterio que `frontend/lib/features/auth/domain/business_vocabulary.dart`,
 * del que se porta lo que el almacén usa: **aquí solo se decide cómo se dice,
 * no qué se enseña**. Si una pantalla sale o no lo deciden los módulos
 * (`negocio.ts`); el vocabulario sale del rubro.
 *
 * **«Insumo» o «producto».** En un restaurante «insumo» es exacto: la papa no
 * se vende, se convierte en plato. En una bodega, una botica, una tienda de
 * ropa o una ferretería casi todo lo que entra al almacén es lo mismo que sale
 * por la caja —mercadería de reventa—, y llamarlo «insumo» obliga a traducir en
 * cada pantalla. Lo poco que sí se gasta sin venderse (las bolsas, los
 * ganchos) también se entiende como «producto». Por eso la tienda dice
 * «producto», igual que el POS (`supply` en el vocabulario de Dart).
 *
 * Las palabras van en minúscula y sin artículo; quien las usa decide la
 * mayúscula con `mayuscula`, para que una frase no tenga que existir dos veces.
 */
export interface Vocabulario {
  /** Lo que guarda el almacén, uno: «insumo» o «producto». */
  insumo: string;
  /** Lo mismo, varios: «insumos» o «productos». */
  insumos: string;
  /** El local entero, como se nombra en una ficha: «restaurante» o «negocio». */
  negocio: string;
  /** Donde se trabaja con lo del almacén: «la cocina» o «la tienda». */
  dondeSeTrabaja: string;
  /** Los sitios a los que apunta una merma que se repite. */
  dondeSePierde: string;
  /** Cómo nombrar bien lo que se da de alta, con su ejemplo. */
  ejemploDeNombre: string;

  /*
   * Las variantes: el mismo producto en varias combinaciones. Nacieron para
   * la ropa y hablaban de tallas en todas partes; la tienda de tecnología,
   * que las usa para el mismo celular en 128 y 256 GB, leía «Por prenda» y
   * «Recibir por talla». Como en el POS (`variant_vocabulary.dart`).
   */

  /** Una combinación: «talla», «versión» o «variante». Siempre femenina. */
  variante: string;
  /** Varias: «tallas», «versiones», «variantes». */
  variantes: string;
  /** Lo que las agrupa, como columna de la hoja: «Prenda», «Modelo», «Producto». */
  familiaColumna: string;
  /** El filtro que las enseña en matriz: «Por prenda». */
  porFamilia: string;
  /** Una pieza que llega del proveedor: «prenda», «equipo» o «unidad». */
  pieza: string;
  /** El primer eje si la hoja no trae ninguno: «Talla», «Capacidad». */
  ejePorOmision: string;
}

/**
 * Las palabras de siempre. El restaurante lee exactamente lo que leía antes de
 * que el almacén conociera el rubro.
 */
export const VOCABULARIO_DE_RESTAURANTE: Vocabulario = {
  insumo: 'insumo',
  insumos: 'insumos',
  negocio: 'restaurante',
  dondeSeTrabaja: 'la cocina',
  dondeSePierde: 'la cámara, el proveedor o la cocina',
  ejemploDeNombre: '«Lomo fino de res», no «lomo»',
  // El restaurante no tiene variantes; si un día las tiene, neutras.
  variante: 'variante',
  variantes: 'variantes',
  familiaColumna: 'Producto',
  porFamilia: 'Por variantes',
  pieza: 'unidad',
  ejePorOmision: 'Variante',
};

/**
 * Una tienda, sea bodega, botica o ferretería: lo que cambia entre ellas no
 * es una palabra del almacén sino un dato del producto. Las variantes, si un
 * día las encienden, con palabras neutras.
 */
export const VOCABULARIO_DE_TIENDA: Vocabulario = {
  insumo: 'producto',
  insumos: 'productos',
  negocio: 'negocio',
  dondeSeTrabaja: 'la tienda',
  dondeSePierde: 'el depósito, el proveedor o el mostrador',
  ejemploDeNombre: 'con su marca y su medida: «Pintura látex blanca 4 L», no «pintura»',
  variante: 'variante',
  variantes: 'variantes',
  familiaColumna: 'Producto',
  porFamilia: 'Por variantes',
  pieza: 'unidad',
  ejePorOmision: 'Variante',
};

/** La tienda de ropa: la tienda, con sus tallas y sus prendas de siempre. */
export const VOCABULARIO_DE_ROPA: Vocabulario = {
  ...VOCABULARIO_DE_TIENDA,
  variante: 'talla',
  variantes: 'tallas',
  familiaColumna: 'Prenda',
  porFamilia: 'Por prenda',
  pieza: 'prenda',
  ejePorOmision: 'Talla',
};

/**
 * La tienda de tecnología: la tienda, con el mismo equipo en varias
 * capacidades y colores. Cada combinación es una «versión» del modelo.
 */
export const VOCABULARIO_DE_TECNOLOGIA: Vocabulario = {
  ...VOCABULARIO_DE_TIENDA,
  variante: 'versión',
  variantes: 'versiones',
  familiaColumna: 'Modelo',
  porFamilia: 'Por modelo',
  pieza: 'equipo',
  ejePorOmision: 'Capacidad',
};

/**
 * El vocabulario de un rubro. Un rubro nuevo que no se nombre aquí no compila:
 * es a propósito, para que nadie lo descubra leyendo «insumo» en su almacén.
 */
export function vocabularioDe(rubro: Rubro): Vocabulario {
  switch (rubro) {
    case 'restaurant':
      return VOCABULARIO_DE_RESTAURANTE;
    case 'retail':
    case 'pharmacy':
    case 'hardware':
      return VOCABULARIO_DE_TIENDA;
    case 'textile':
      return VOCABULARIO_DE_ROPA;
    case 'technology':
      return VOCABULARIO_DE_TECNOLOGIA;
  }
}

/** La palabra con la primera letra en mayúscula, para un título o un botón. */
export function mayuscula(palabra: string): string {
  return palabra === '' ? palabra : palabra[0]!.toUpperCase() + palabra.slice(1);
}

/**
 * El negocio de la sesión abierta, para las pantallas.
 *
 * Por omisión, el restaurante con todo: una pantalla que se pinte fuera de la
 * aplicación —una prueba, una vista previa— dice lo de siempre.
 */
export const NegocioContexto = createContext<Negocio>(RESTAURANTE_CON_TODO);

export function useNegocio(): Negocio {
  return useContext(NegocioContexto);
}

export function useVocabulario(): Vocabulario {
  return vocabularioDe(useNegocio().rubro);
}
